import { clearToastTimer } from '#pplx/frontend/aether/toasts/utils/clearToastTimer.js';
import { limitAfterOpen } from '#pplx/frontend/aether/toasts/utils/limitAfterOpen.js';
import { promoteOldestLimited } from '#pplx/frontend/aether/toasts/utils/promoteOldestLimited.js';
import { scheduleToastTimer } from '#pplx/frontend/aether/toasts/utils/scheduleToastTimer.js';
import { updateExpirationOrder } from '#pplx/frontend/aether/toasts/utils/updateExpirationOrder.js';
import { createStore } from 'zustand/vanilla';
const INITIAL_STATE = {
    toasts: new Map(),
    displayOrder: new Set(),
    expirationOrder: new Set(),
};
export const createToastStore = (opts) => {
    let nextInstanceVersion = 0;
    const storeCreator = (set, get) => ({
        ...INITIAL_STATE,
        resetStore: () => {
            get().toasts.forEach((toast) => clearToastTimer(toast));
            set((prev) => ({ ...prev, ...INITIAL_STATE }));
        },
        generateId: opts.generateId,
        addToast: (params) => {
            const id = params.dedupKey ?? get().generateId();
            const existingToast = get().toasts.get(id);
            if (existingToast != null && !existingToast.ending) {
                const { ending, expiresAt, instanceVersion, limited, timer } = existingToast;
                const updatedToast = scheduleToastTimer({
                    ...existingToast,
                    ...params,
                    id,
                    instanceVersion,
                    ending,
                    limited,
                    timer,
                    expiresAt,
                }, () => get().closeToast({ id, reason: 'timeout' }));
                const updatedToasts = new Map(get().toasts);
                updatedToasts.set(id, updatedToast);
                const updatedExpirationOrder = updateExpirationOrder(get().expirationOrder, updatedToasts, updatedToast);
                set((prev) => ({
                    ...prev,
                    toasts: updatedToasts,
                    expirationOrder: updatedExpirationOrder,
                }));
                return updatedToast;
            }
            const toast = scheduleToastTimer({
                ...params,
                id,
                instanceVersion: ++nextInstanceVersion,
                ending: false,
                limited: false,
                timer: null,
                expiresAt: null,
            }, () => get().closeToast({ id, reason: 'timeout' }));
            const updatedToasts = new Map(get().toasts);
            const updatedDisplayOrder = new Set(get().displayOrder);
            if (existingToast != null) {
                updatedToasts.delete(id);
                updatedDisplayOrder.delete(id);
            }
            updatedToasts.set(id, toast);
            updatedDisplayOrder.add(id);
            const updatedExpirationOrder = updateExpirationOrder(get().expirationOrder, updatedToasts, toast);
            set((prev) => ({
                ...prev,
                ...limitAfterOpen({
                    toasts: updatedToasts,
                    displayOrder: updatedDisplayOrder,
                    expirationOrder: updatedExpirationOrder,
                }, opts.maxVisibleToasts),
            }));
            return toast;
        },
        getToastById: ({ id }) => {
            return get().toasts.get(id) ?? null;
        },
        getToastIds: () => {
            return Array.from(get().displayOrder);
        },
        updateToast: ({ id, params }) => {
            const toast = get().toasts.get(id);
            if (toast == null || toast.ending) {
                return null;
            }
            const patchedToast = {
                ...toast,
                ...params,
            };
            const updatesTimeout = Object.prototype.hasOwnProperty.call(params, 'timeoutSeconds');
            const updatedToast = updatesTimeout
                ? scheduleToastTimer(patchedToast, () => get().closeToast({ id, reason: 'timeout' }))
                : patchedToast;
            const updatedToasts = new Map(get().toasts);
            updatedToasts.set(id, updatedToast);
            const updatedExpirationOrder = updatesTimeout
                ? updateExpirationOrder(get().expirationOrder, updatedToasts, updatedToast)
                : get().expirationOrder;
            set((prev) => ({
                ...prev,
                toasts: updatedToasts,
                expirationOrder: updatedExpirationOrder,
            }));
            return updatedToast;
        },
        closeToast: ({ id, reason = 'programmatic' }) => {
            const toast = get().toasts.get(id);
            if (toast == null || toast.ending) {
                return null;
            }
            const endingToast = {
                ...clearToastTimer(toast),
                ending: true,
            };
            const updatedToasts = new Map(get().toasts);
            updatedToasts.set(id, endingToast);
            const updatedState = {
                toasts: updatedToasts,
                displayOrder: get().displayOrder,
                expirationOrder: updateExpirationOrder(get().expirationOrder, updatedToasts, endingToast),
            };
            set((prev) => ({
                ...prev,
                ...(toast.limited ? updatedState : promoteOldestLimited(updatedState)),
            }));
            toast.onClose?.({ reason });
            return endingToast;
        },
        closeAllToasts: ({ reason = 'programmatic' } = {}) => {
            const openToasts = Array.from(get().toasts.values()).filter((toast) => !toast.ending);
            if (openToasts.length === 0) {
                return;
            }
            const updatedToasts = new Map(get().toasts);
            openToasts.forEach((toast) => {
                updatedToasts.set(toast.id, {
                    ...clearToastTimer(toast),
                    ending: true,
                });
            });
            set((prev) => ({ ...prev, toasts: updatedToasts, expirationOrder: new Set() }));
            openToasts.forEach((toast) => toast.onClose?.({ reason }));
        },
        removeToast: ({ id }) => {
            const toast = get().toasts.get(id);
            if (toast == null) {
                return null;
            }
            clearToastTimer(toast);
            const updatedToasts = new Map(get().toasts);
            updatedToasts.delete(id);
            const updatedDisplayOrder = new Set(get().displayOrder);
            updatedDisplayOrder.delete(id);
            const updatedExpirationOrder = new Set(get().expirationOrder);
            updatedExpirationOrder.delete(id);
            const updatedState = {
                toasts: updatedToasts,
                displayOrder: updatedDisplayOrder,
                expirationOrder: updatedExpirationOrder,
            };
            set((prev) => ({
                ...prev,
                ...(toast.ending || toast.limited ? updatedState : promoteOldestLimited(updatedState)),
            }));
            return toast;
        },
    });
    return createStore()(storeCreator);
};
//# sourceMappingURL=createToastStore.js.map